/** Normalized connector field metadata for schema discovery and mapping UI. */

export type ConnectorFieldType =
  | "string"
  | "email"
  | "phone"
  | "number"
  | "boolean"
  | "date"
  | "datetime"
  | "enum";

export interface ConnectorField {
  key: string;
  label: string;
  type: ConnectorFieldType;
  required?: boolean;
  /** Destination fields presented for mapping must be writable. */
  writable?: boolean;
  /** Source fields are generally readable. */
  readable?: boolean;
  description?: string;
  aliases?: string[];
  /** Provider-native id when different from `key` (e.g. Neon custom field id). */
  externalId?: string;
  /** True when the field came from a live discovery poll. */
  discovered?: boolean;
}

export interface ConnectorCapabilities {
  supportsEventTriggeredSync?: boolean;
  supportsScheduledSync?: boolean;
  supportsSchemaDiscovery?: boolean;
}

export interface ConnectorSchema {
  provider: string;
  label: string;
  kind: "source" | "destination";
  capabilities: ConnectorCapabilities;
  fields: ConnectorField[];
  /** Optional connector-authored pairs used before heuristic matching. */
  recommendedMappings?: Array<{ sourceKey: string; destinationKey: string }>;
  /** ISO timestamp when this schema was live-polled. */
  discoveredAt?: string;
  /** `live` when polled from the provider; `static` for catalog fallback. */
  discoverySource?: "live" | "static";
}

export type FieldMappingStatus =
  "matched" | "suggested" | "unmapped" | "required";

export interface FieldMapping {
  sourceField: string;
  destinationField: string | null;
  status: FieldMappingStatus;
  ignored?: boolean;
}

export type SyncFrequency =
  "after_event" | "once" | "hourly" | "daily" | "weekly" | "monthly" | "custom";

export type DayOfWeek = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export interface SyncScheduleConfig {
  frequency: SyncFrequency;
  timezone?: string;
  date?: string;
  time?: string;
  interval?: number;
  daysOfWeek?: DayOfWeek[];
  dayOfMonth?: number;
  eventDelayMinutes?: number;
  retryIfUnavailable?: boolean;
  /** Custom schedule unit when frequency is `custom`. */
  customUnit?: "minutes" | "hours" | "days";
}

export type ExistingRecordAction = "update" | "skip" | "create";
export type EmptySourceValueAction = "preserve" | "clear";

export type MatchStrategy =
  "email" | "destination_id" | "email_and_name" | "custom";

export interface SyncBehaviorConfig {
  existingRecordAction: ExistingRecordAction;
  matchStrategy: MatchStrategy;
  emptySourceValueAction: EmptySourceValueAction;
}

export interface ConfigureSyncValidation {
  ok: boolean;
  scheduleErrors: string[];
  mappingErrors: string[];
  behaviorErrors: string[];
}

const TYPE_COMPAT: Record<
  ConnectorFieldType,
  ReadonlySet<ConnectorFieldType>
> = {
  string: new Set(["string", "email", "phone", "enum"]),
  email: new Set(["email", "string"]),
  phone: new Set(["phone", "string"]),
  number: new Set(["number"]),
  boolean: new Set(["boolean"]),
  date: new Set(["date", "datetime"]),
  datetime: new Set(["datetime", "date"]),
  enum: new Set(["enum", "string"]),
};

/** Strip separators/case for name comparison. */
export function normalizeFieldKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

const GLOBAL_ALIASES: Record<string, string[]> = {
  firstname: ["firstname", "fname", "givenname", "first"],
  lastname: ["lastname", "lname", "surname", "familyname", "last"],
  email: ["email", "emailaddress", "email1", "primaryemail", "mail"],
  phone: ["phone", "phonenumber", "mobile", "cellphone", "telephone"],
  company: [
    "company",
    "organization",
    "organisation",
    "accountname",
    "companyname",
    "org",
  ],
  jobtitle: ["jobtitle", "title", "position", "role"],
  meetingid: ["meetingid", "eventid", "zoommeetingid", "webinarid"],
  meetingtopic: [
    "meetingtopic",
    "eventname",
    "eventtitle",
    "topic",
    "meetingname",
  ],
  jointime: ["jointime", "attendancestart", "checkintime", "joinedat"],
  leavetime: ["leavetime", "attendanceend", "checkouttime", "leftat"],
  duration: ["duration", "attendanceduration", "totalduration", "minutes"],
  participantuuid: [
    "participantuuid",
    "participantid",
    "registrantid",
    "attendeeid",
  ],
  destinationid: [
    "destinationid",
    "recordid",
    "accountid",
    "contactid",
    "crmid",
  ],
  address: ["address", "street", "streetaddress", "mailingstreet"],
  city: ["city", "town", "mailingcity"],
  state: ["state", "province", "region", "mailingstate"],
  zip: ["zip", "postalcode", "zipcode", "mailingpostal"],
  country: ["country", "countrycode", "mailingcountry"],
  industry: ["industry", "sector"],
  comments: ["comments", "notes", "comment", "description"],
};

function aliasBucket(normalized: string): string | null {
  for (const [canonical, aliases] of Object.entries(GLOBAL_ALIASES)) {
    if (aliases.includes(normalized) || normalized === canonical)
      return canonical;
  }
  return null;
}

export function fieldsAreCompatible(
  sourceType: ConnectorFieldType,
  destinationType: ConnectorFieldType,
): boolean {
  return TYPE_COMPAT[sourceType]?.has(destinationType) ?? false;
}

function fieldTokens(field: ConnectorField): Set<string> {
  const tokens = new Set<string>();
  tokens.add(normalizeFieldKey(field.key));
  tokens.add(normalizeFieldKey(field.label));
  for (const alias of field.aliases ?? []) {
    tokens.add(normalizeFieldKey(alias));
  }
  const bucket = aliasBucket(normalizeFieldKey(field.key));
  if (bucket) tokens.add(bucket);
  const labelBucket = aliasBucket(normalizeFieldKey(field.label));
  if (labelBucket) tokens.add(labelBucket);
  return tokens;
}

function scorePair(
  source: ConnectorField,
  destination: ConnectorField,
): number {
  if (!fieldsAreCompatible(source.type, destination.type)) return 0;

  const sourceTokens = fieldTokens(source);
  const destTokens = fieldTokens(destination);
  const sourceNorm = normalizeFieldKey(source.key);
  const destNorm = normalizeFieldKey(destination.key);

  if (sourceNorm === destNorm) return 100;

  for (const token of sourceTokens) {
    if (destTokens.has(token)) {
      const sourceBucket = aliasBucket(sourceNorm);
      const destBucket = aliasBucket(destNorm);
      if (sourceBucket && destBucket && sourceBucket === destBucket) return 92;
      return 80;
    }
  }

  // Soft lexical overlap on normalized keys (e.g. registrantCompany → company)
  if (sourceNorm.includes(destNorm) || destNorm.includes(sourceNorm)) {
    if (Math.min(sourceNorm.length, destNorm.length) >= 4) return 55;
  }

  return 0;
}

/**
 * Propose source→destination mappings using recommended pairs, exact/alias
 * matches, then semantic similarity. Does not map incompatible types.
 */
export function proposeFieldMappings(
  sourceFields: ConnectorField[],
  destinationFields: ConnectorField[],
  recommendedMappings: Array<{
    sourceKey: string;
    destinationKey: string;
  }> = [],
): FieldMapping[] {
  const sourceByKey = new Map(sourceFields.map((f) => [f.key, f]));
  const destByKey = new Map(destinationFields.map((f) => [f.key, f]));
  const writableDest = destinationFields.filter((f) => f.writable !== false);
  const usedDest = new Set<string>();
  const mappings: FieldMapping[] = [];

  for (const rec of recommendedMappings) {
    const source = sourceByKey.get(rec.sourceKey);
    const dest = destByKey.get(rec.destinationKey);
    if (!source || !dest || dest.writable === false) continue;
    if (usedDest.has(dest.key)) continue;
    if (!fieldsAreCompatible(source.type, dest.type)) continue;
    usedDest.add(dest.key);
    mappings.push({
      sourceField: source.key,
      destinationField: dest.key,
      status: "matched",
    });
  }

  const mappedSources = new Set(mappings.map((m) => m.sourceField));
  const scored: Array<{
    source: ConnectorField;
    dest: ConnectorField;
    score: number;
  }> = [];

  for (const source of sourceFields) {
    if (mappedSources.has(source.key)) continue;
    for (const dest of writableDest) {
      if (usedDest.has(dest.key)) continue;
      const score = scorePair(source, dest);
      if (score > 0) scored.push({ source, dest, score });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  for (const entry of scored) {
    if (mappedSources.has(entry.source.key) || usedDest.has(entry.dest.key))
      continue;
    mappedSources.add(entry.source.key);
    usedDest.add(entry.dest.key);
    mappings.push({
      sourceField: entry.source.key,
      destinationField: entry.dest.key,
      status: entry.score >= 80 ? "matched" : "suggested",
    });
  }

  for (const source of sourceFields) {
    if (mappedSources.has(source.key)) continue;
    mappings.push({
      sourceField: source.key,
      destinationField: null,
      status: "unmapped",
    });
  }

  for (const dest of writableDest) {
    if (!dest.required) continue;
    const covered = mappings.some(
      (m) => m.destinationField === dest.key && !m.ignored && m.sourceField,
    );
    if (!covered) {
      mappings.push({
        sourceField: "",
        destinationField: dest.key,
        status: "required",
      });
    }
  }

  return mappings;
}

/** Keep mappings that still resolve against current schemas; drop invalid rows. */
export function reconcileFieldMappings(
  existing: FieldMapping[],
  sourceFields: ConnectorField[],
  destinationFields: ConnectorField[],
  recommendedMappings: Array<{
    sourceKey: string;
    destinationKey: string;
  }> = [],
): FieldMapping[] {
  const sourceKeys = new Set(sourceFields.map((f) => f.key));
  const destByKey = new Map(destinationFields.map((f) => [f.key, f]));
  const preserved: FieldMapping[] = [];
  const usedSources = new Set<string>();
  const usedDest = new Set<string>();

  for (const row of existing) {
    if (row.ignored) {
      if (row.sourceField && sourceKeys.has(row.sourceField)) {
        preserved.push({ ...row, status: "unmapped", destinationField: null });
        usedSources.add(row.sourceField);
      }
      continue;
    }
    if (!row.sourceField || !sourceKeys.has(row.sourceField)) continue;
    if (!row.destinationField) {
      preserved.push({ ...row, status: "unmapped", destinationField: null });
      usedSources.add(row.sourceField);
      continue;
    }
    const dest = destByKey.get(row.destinationField);
    if (!dest || dest.writable === false) continue;
    const source = sourceFields.find((f) => f.key === row.sourceField);
    if (!source || !fieldsAreCompatible(source.type, dest.type)) continue;
    if (usedDest.has(dest.key)) continue;
    usedSources.add(row.sourceField);
    usedDest.add(dest.key);
    preserved.push({
      ...row,
      status: row.status === "suggested" ? "suggested" : "matched",
    });
  }

  const proposed = proposeFieldMappings(
    sourceFields,
    destinationFields,
    recommendedMappings,
  );
  for (const row of proposed) {
    if (row.status === "required") {
      const already = preserved.some(
        (m) => m.destinationField === row.destinationField && !m.ignored,
      );
      if (!already) preserved.push(row);
      continue;
    }
    if (!row.sourceField || usedSources.has(row.sourceField)) continue;
    if (row.destinationField && usedDest.has(row.destinationField)) {
      preserved.push({
        sourceField: row.sourceField,
        destinationField: null,
        status: "unmapped",
      });
      usedSources.add(row.sourceField);
      continue;
    }
    preserved.push(row);
    usedSources.add(row.sourceField);
    if (row.destinationField) usedDest.add(row.destinationField);
  }

  return preserved;
}

export function defaultSchedule(
  capabilities: ConnectorCapabilities,
  timezone?: string,
): SyncScheduleConfig {
  const tz =
    timezone ??
    (typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "UTC");
  if (capabilities.supportsEventTriggeredSync) {
    return {
      frequency: "after_event",
      timezone: tz,
      eventDelayMinutes: 15,
      retryIfUnavailable: true,
    };
  }
  return {
    frequency: "custom",
    timezone: tz,
    interval: 15,
    customUnit: "minutes",
  };
}

export function defaultBehavior(): SyncBehaviorConfig {
  return {
    existingRecordAction: "update",
    matchStrategy: "email",
    emptySourceValueAction: "preserve",
  };
}

export function availableMatchStrategies(
  sourceFields: ConnectorField[],
  destinationFields: ConnectorField[],
): MatchStrategy[] {
  const sourceKeys = new Set(sourceFields.map((f) => normalizeFieldKey(f.key)));
  const destKeys = new Set(
    destinationFields.map((f) => normalizeFieldKey(f.key)),
  );
  const hasEmail =
    [...sourceKeys].some((k) => aliasBucket(k) === "email") &&
    [...destKeys].some((k) => aliasBucket(k) === "email");
  const hasName =
    [...sourceKeys].some((k) => aliasBucket(k) === "firstname") &&
    [...sourceKeys].some((k) => aliasBucket(k) === "lastname") &&
    [...destKeys].some((k) => aliasBucket(k) === "firstname") &&
    [...destKeys].some((k) => aliasBucket(k) === "lastname");
  const hasDestId = [...destKeys].some(
    (k) => aliasBucket(k) === "destinationid" || k === "id" || k.endsWith("id"),
  );

  const strategies: MatchStrategy[] = [];
  if (hasEmail) strategies.push("email");
  if (hasDestId) strategies.push("destination_id");
  if (hasEmail && hasName) strategies.push("email_and_name");
  strategies.push("custom");
  return strategies;
}

/** Convert a schedule config into seconds for the existing interval scheduler when applicable. */
export function scheduleToIntervalSeconds(
  schedule: SyncScheduleConfig,
): number | null {
  switch (schedule.frequency) {
    case "after_event":
    case "once":
      return null;
    case "hourly":
      return Math.max(1, schedule.interval ?? 1) * 3600;
    case "daily":
      return Math.max(1, schedule.interval ?? 1) * 86400;
    case "weekly":
      return 7 * 86400;
    case "monthly":
      return 30 * 86400;
    case "custom": {
      const n = Math.max(1, schedule.interval ?? 1);
      if (schedule.customUnit === "days") return n * 86400;
      if (schedule.customUnit === "hours") return n * 3600;
      return n * 60;
    }
    default:
      return null;
  }
}

export function validateSchedule(
  schedule: SyncScheduleConfig,
  capabilities: ConnectorCapabilities,
): string[] {
  const errors: string[] = [];
  if (schedule.frequency === "after_event") {
    if (!capabilities.supportsEventTriggeredSync) {
      errors.push("This source does not support event-triggered sync.");
    }
    const delay = schedule.eventDelayMinutes ?? 15;
    if (!Number.isFinite(delay) || delay < 0 || delay > 24 * 60) {
      errors.push("Enter a delay between 0 and 1440 minutes.");
    }
    return errors;
  }
  if (!capabilities.supportsScheduledSync && schedule.frequency !== "once") {
    errors.push("This source does not support scheduled sync.");
  }
  if (!schedule.timezone?.trim()) {
    errors.push("Select a time zone.");
  }
  if (schedule.frequency === "once") {
    if (!schedule.date?.trim())
      errors.push("Choose a date for a one-time sync.");
    if (!schedule.time?.trim())
      errors.push("Choose a time for a one-time sync.");
  }
  if (
    schedule.frequency === "hourly" ||
    schedule.frequency === "daily" ||
    schedule.frequency === "custom"
  ) {
    const interval = schedule.interval ?? 0;
    if (!Number.isFinite(interval) || interval < 1) {
      errors.push("Interval must be at least 1.");
    }
  }
  if (
    schedule.frequency === "hourly" ||
    schedule.frequency === "daily" ||
    schedule.frequency === "weekly" ||
    schedule.frequency === "monthly"
  ) {
    if (!schedule.time?.trim()) errors.push("Choose a time of day.");
  }
  if (schedule.frequency === "weekly") {
    if (!schedule.daysOfWeek?.length)
      errors.push("Select at least one day of the week.");
  }
  if (schedule.frequency === "monthly") {
    const day = schedule.dayOfMonth ?? 0;
    if (!Number.isInteger(day) || day < 1 || day > 31) {
      errors.push("Day of month must be between 1 and 31.");
    }
  }
  if (schedule.frequency === "custom" && !schedule.customUnit) {
    errors.push("Choose a custom interval unit.");
  }
  return errors;
}

export function validateMappings(
  mappings: FieldMapping[],
  sourceFields: ConnectorField[],
  destinationFields: ConnectorField[],
): string[] {
  const errors: string[] = [];
  const sourceByKey = new Map(sourceFields.map((f) => [f.key, f]));
  const destByKey = new Map(destinationFields.map((f) => [f.key, f]));
  const usedDest = new Map<string, string>();

  for (const row of mappings) {
    if (row.ignored) continue;
    if (
      row.status === "required" ||
      (!row.sourceField && row.destinationField)
    ) {
      const dest = row.destinationField
        ? destByKey.get(row.destinationField)
        : undefined;
      if (dest?.required) {
        const covered = mappings.some(
          (m) =>
            !m.ignored &&
            m.destinationField === dest.key &&
            m.sourceField &&
            sourceByKey.has(m.sourceField),
        );
        if (!covered) {
          errors.push(`${dest.label} is required and needs a source field.`);
        }
      }
      continue;
    }
    if (!row.sourceField) continue;
    const source = sourceByKey.get(row.sourceField);
    if (!source) {
      errors.push(`Source field “${row.sourceField}” is no longer available.`);
      continue;
    }
    if (!row.destinationField) continue;
    const dest = destByKey.get(row.destinationField);
    if (!dest || dest.writable === false) {
      errors.push(
        `Destination field “${row.destinationField}” is not writable.`,
      );
      continue;
    }
    if (!fieldsAreCompatible(source.type, dest.type)) {
      errors.push(
        `${source.label} cannot map to ${dest.label} (incompatible types).`,
      );
    }
    const prior = usedDest.get(dest.key);
    if (prior && prior !== row.sourceField) {
      errors.push(`${dest.label} is mapped more than once.`);
    }
    usedDest.set(dest.key, row.sourceField);
  }

  for (const dest of destinationFields) {
    if (!dest.required || dest.writable === false) continue;
    const covered = mappings.some(
      (m) =>
        !m.ignored &&
        m.destinationField === dest.key &&
        Boolean(m.sourceField) &&
        sourceByKey.has(m.sourceField),
    );
    if (!covered) {
      errors.push(`${dest.label} is required and needs a source field.`);
    }
  }

  return [...new Set(errors)];
}

export function validateBehavior(
  behavior: SyncBehaviorConfig,
  sourceFields: ConnectorField[],
  destinationFields: ConnectorField[],
): string[] {
  const errors: string[] = [];
  const available = availableMatchStrategies(sourceFields, destinationFields);
  if (!available.includes(behavior.matchStrategy)) {
    errors.push(
      "Selected matching strategy is not supported for these connectors.",
    );
  }
  return errors;
}

export function validateConfigureSync(input: {
  schedule: SyncScheduleConfig;
  mappings: FieldMapping[];
  behavior: SyncBehaviorConfig;
  sourceSchema: ConnectorSchema;
  destinationSchema: ConnectorSchema;
}): ConfigureSyncValidation {
  const scheduleErrors = validateSchedule(
    input.schedule,
    input.sourceSchema.capabilities,
  );
  const mappingErrors = validateMappings(
    input.mappings,
    input.sourceSchema.fields,
    input.destinationSchema.fields.filter((f) => f.writable !== false),
  );
  const behaviorErrors = validateBehavior(
    input.behavior,
    input.sourceSchema.fields,
    input.destinationSchema.fields,
  );
  return {
    ok:
      scheduleErrors.length === 0 &&
      mappingErrors.length === 0 &&
      behaviorErrors.length === 0,
    scheduleErrors,
    mappingErrors,
    behaviorErrors,
  };
}

export function summarizeConfigureSync(input: {
  schedule: SyncScheduleConfig;
  mappings: FieldMapping[];
  behavior: SyncBehaviorConfig;
}): {
  schedule: string;
  dataMapping: string;
  existingContacts: string;
  matchBy: string;
  emptyValues: string;
} {
  const mapped = input.mappings.filter(
    (m) => m.destinationField && !m.ignored && m.sourceField,
  ).length;
  const ignored = input.mappings.filter((m) => m.ignored).length;
  const schedule = (() => {
    switch (input.schedule.frequency) {
      case "after_event":
        return `After each meeting, ${input.schedule.eventDelayMinutes ?? 15} minutes after completion`;
      case "once":
        return `Once on ${input.schedule.date ?? "—"} at ${input.schedule.time ?? "—"}`;
      case "hourly":
        return `Every ${input.schedule.interval ?? 1} hour(s)`;
      case "daily":
        return `Every ${input.schedule.interval ?? 1} day(s) at ${input.schedule.time ?? "—"}`;
      case "weekly":
        return `Weekly on ${(input.schedule.daysOfWeek ?? []).join(", ") || "—"} at ${input.schedule.time ?? "—"}`;
      case "monthly":
        return `Monthly on day ${input.schedule.dayOfMonth ?? "—"} at ${input.schedule.time ?? "—"}`;
      case "custom":
        return `Every ${input.schedule.interval ?? 1} ${input.schedule.customUnit ?? "minutes"}`;
      default:
        return "Not configured";
    }
  })();

  const existing =
    input.behavior.existingRecordAction === "update"
      ? "Update existing contacts"
      : input.behavior.existingRecordAction === "skip"
        ? "Skip matching contacts"
        : "Create a new record";

  const matchBy =
    input.behavior.matchStrategy === "email"
      ? "Email address"
      : input.behavior.matchStrategy === "destination_id"
        ? "Destination record ID"
        : input.behavior.matchStrategy === "email_and_name"
          ? "Email + Name"
          : "Custom matching rules";

  return {
    schedule,
    dataMapping: `${mapped} field${mapped === 1 ? "" : "s"} mapped${ignored ? ` · ${ignored} ignored` : ""}`,
    existingContacts: existing,
    matchBy,
    emptyValues:
      input.behavior.emptySourceValueAction === "preserve"
        ? "Preserve existing CRM values"
        : "Clear the destination value",
  };
}
